/**
 * InputSourcePanel — Multi-source document capture orchestrator
 *
 * Replaces the single ChaosDocumentUploader in DocumentStudioPage's "upload" tab.
 * Presents 4 modality cards at the top; selected modality expands below.
 *
 * Modalities:
 *   FILE      — drag-and-drop via ChaosDocumentUploader (existing, unchanged)
 *   PHOTO     — live browser camera capture (LivePhotoCapture)
 *   VOICE     — browser mic recording (VoiceRecorder)
 *   WEARABLES — import from Vuzix captures (WearablesCaptureImporter)
 *
 * All modalities share the same onUploadComplete callback contract:
 *   (results: Array<{ document: { documentId: string } }>) => void
 *
 * Pattern: PhotoComplianceUpload area-selection cards → uploader below
 * Architecture: Mantine v8.x only. Repository pattern (all data via hooks).
 * DRR-128: write-time minimization — capture only what is needed.
 */

import React, { useState } from 'react';
import {
  Stack,
  SimpleGrid,
  Card,
  Text,
  Alert,
} from '@mantine/core';
import {
  IconUpload,
  IconCamera,
  IconMicrophone,
  IconDeviceMobileVibration,
  IconInfoCircle,
} from '@tabler/icons-react';
import { ModalityCard } from './ModalityCard';
import { ChaosDocumentUploader } from './ChaosDocumentUploader';
import { LivePhotoCapture } from './LivePhotoCapture';
import { VoiceRecorder } from './VoiceRecorder';
import { WearablesCaptureImporter } from './WearablesCaptureImporter';
import type { UploadResult } from './hooks/useChaosDocumentUpload';

// ============================================================
// Types
// ============================================================

export type SourceModality = 'FILE' | 'PHOTO' | 'VOICE' | 'WEARABLES';

export interface InputSourcePanelProps {
  /**
   * Called when any modality completes a successful upload/import.
   * Matches the exact signature expected by DocumentStudioPage.handleUploadComplete.
   */
  onUploadComplete?: (results: UploadResult[]) => void;

  /**
   * Called when an upload/capture fails.
   */
  onUploadError?: (error: Error) => void;

  /**
   * Source system tag passed to the audit trail.
   */
  sourceSystem?: string;

  /**
   * Optional additional metadata for audit.
   */
  metadata?: Record<string, unknown>;
}

// ============================================================
// Modality definitions
// ============================================================

const MODALITIES: Array<{
  id: SourceModality;
  label: string;
  description: string;
  icon: React.ReactNode;
  comingSoon: boolean;
}> = [
  {
    id: 'FILE',
    label: 'File Upload',
    description: 'Drag & drop or browse — PDF, DOCX, TXT, CSV, JSON, XML, MD',
    icon: <IconUpload size={16} />,
    comingSoon: false,
  },
  {
    id: 'PHOTO',
    label: 'Live Photo',
    description: 'Capture with your device camera for instant vision analysis',
    icon: <IconCamera size={16} />,
    comingSoon: false,
  },
  {
    id: 'VOICE',
    label: 'Voice Recording',
    description: 'Record a voice note — automatically transcribed and analyzed',
    icon: <IconMicrophone size={16} />,
    comingSoon: false,
  },
  {
    id: 'WEARABLES',
    label: 'Wearables (Vuzix)',
    description: 'Import photos, voice notes, and scans from your Vuzix glasses',
    icon: <IconDeviceMobileVibration size={16} />,
    comingSoon: false,
  },
];

// ============================================================
// Component
// ============================================================

export const InputSourcePanel: React.FC<InputSourcePanelProps> = ({
  onUploadComplete,
  onUploadError,
  sourceSystem,
  metadata,
}) => {
  const [activeModality, setActiveModality] = useState<SourceModality>('FILE');

  return (
    <Stack gap="md">
      {/* Modality selector cards */}
      <SimpleGrid cols={{ base: 2, sm: 4 }}>
        {MODALITIES.map((m) => (
          <ModalityCard
            key={m.id}
            icon={m.icon}
            label={m.label}
            description={m.description}
            isActive={activeModality === m.id}
            onClick={() => setActiveModality(m.id)}
            comingSoon={m.comingSoon}
          />
        ))}
      </SimpleGrid>

      {/* Active capture panel */}
      {activeModality === 'FILE' && (
        <ChaosDocumentUploader
          onUploadComplete={onUploadComplete}
          onUploadError={onUploadError}
          sourceSystem={sourceSystem ?? 'document-studio-file'}
          metadata={metadata}
        />
      )}

      {activeModality === 'PHOTO' && (
        <LivePhotoCapture
          onUploadComplete={onUploadComplete}
          onError={onUploadError}
          sourceSystem={sourceSystem ?? 'document-studio-photo'}
        />
      )}

      {activeModality === 'VOICE' && (
        <VoiceRecorder
          onUploadComplete={onUploadComplete}
          onError={onUploadError}
          sourceSystem={sourceSystem ?? 'document-studio-voice'}
        />
      )}

      {activeModality === 'WEARABLES' && (
        <WearablesCaptureImporter
          onImportComplete={onUploadComplete}
          onError={onUploadError}
        />
      )}
    </Stack>
  );
};
